(() => {
  const SELECTORS = {
    root: "[data-ctr-science]",
    nav: "[data-ctr-science-nav]",
    dropdown: "[data-ctr-science-dropdown]",
    dropdownCurrent: "[data-ctr-science-dropdown-current]",
    tab: "[data-ctr-science-tab]",
    panel: "[data-ctr-science-panel]",
    video: "[data-ctr-science-video]",
    progress: "[data-ctr-science-progress]",
  };

  const instances = new Map();

  function pauseOtherVideos(currentVideo) {
    document.querySelectorAll("video").forEach((video) => {
      if (video === currentVideo) return;
      video.pause();
    });
  }

  class CtrScience {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId || "";
      this.nav = section.querySelector(SELECTORS.nav);
      this.dropdown = section.querySelector(SELECTORS.dropdown);
      this.dropdownCurrent = section.querySelector(SELECTORS.dropdownCurrent);
      this.tabs = Array.from(section.querySelectorAll(SELECTORS.tab));
      this.panels = Array.from(section.querySelectorAll(SELECTORS.panel));
      this.videos = Array.from(section.querySelectorAll(SELECTORS.video));
      this.progressBars = Array.from(section.querySelectorAll(SELECTORS.progress));
      this.progressRaf = null;
      this.activeVideo = null;
      this.activeProgress = null;

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.handleDropdownClick = this.handleDropdownClick.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleVideoPlay = this.handleVideoPlay.bind(this);
      this.handleVideoPause = this.handleVideoPause.bind(this);
      this.handleProgressClick = this.handleProgressClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onResize = this.onResize.bind(this);

      this.bind();
      this.syncDropdown();
      this.playActiveVideos();
    }

    bind() {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.handleTabClick);
        tab.addEventListener("keydown", this.handleTabKeydown);
      });

      if (this.dropdown) {
        this.dropdown.addEventListener("click", this.handleDropdownClick);
        document.addEventListener("click", this.handleDocumentClick);
        document.addEventListener("keydown", this.handleDocumentKeydown);
      }

      this.videos.forEach((video) => {
        video.addEventListener("play", this.handleVideoPlay);
        video.addEventListener("pause", this.handleVideoPause);
      });

      this.progressBars.forEach((progress) => {
        progress.addEventListener("click", this.handleProgressClick);
      });

      window.addEventListener("resize", this.onResize);
      document.addEventListener("shopify:section:select", this.onSectionSelect);
      document.addEventListener("shopify:section:deselect", this.onSectionSelect);
      document.addEventListener("shopify:block:select", this.onBlockSelect);
    }

    handleTabClick(event) {
      event.preventDefault();
      this.setActiveTab(event.currentTarget.dataset.tabTarget);
      this.closeNav();
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      const keyMap = {
        ArrowLeft: -1,
        ArrowUp: -1,
        ArrowRight: 1,
        ArrowDown: 1,
      };

      if (event.key === "Home") {
        event.preventDefault();
        this.tabs[0]?.focus();
        return;
      }

      if (event.key === "End") {
        event.preventDefault();
        this.tabs[this.tabs.length - 1]?.focus();
        return;
      }

      const direction = keyMap[event.key];
      if (!direction) return;

      event.preventDefault();
      const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
      this.tabs[nextIndex]?.focus();
    }

    handleDropdownClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const isOpen = this.nav?.classList.toggle("is-open");
      this.dropdown.setAttribute("aria-expanded", isOpen ? "true" : "false");
    }

    handleDocumentClick(event) {
      if (!this.nav || this.nav.contains(event.target)) return;
      this.closeNav();
    }

    handleDocumentKeydown(event) {
      if (event.key !== "Escape") return;
      this.closeNav();
    }

    closeNav() {
      if (!this.nav || !this.dropdown) return;
      this.nav.classList.remove("is-open");
      this.dropdown.setAttribute("aria-expanded", "false");
    }

    setActiveTab(targetId) {
      if (!targetId) return;

      this.pauseSectionVideos();

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.tabTarget === targetId;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.tabPanel === targetId;
        panel.classList.toggle("is-active", isActive);
        if (isActive) {
          panel.removeAttribute("hidden");
        } else {
          panel.setAttribute("hidden", "");
        }
      });

      this.syncDropdown();
      this.playActiveVideos();
    }

    handleVideoPlay(event) {
      pauseOtherVideos(event.currentTarget);
      if (event.currentTarget !== this.activeVideo) return;
      if (!this.progressRaf) this.startProgressLoop();
    }

    handleVideoPause(event) {
      if (event.currentTarget !== this.activeVideo) return;
      this.stopProgressLoop();
      this.updateProgress();
    }

    pauseSectionVideos() {
      this.videos.forEach((video) => {
        video.pause();
      });
    }

    getActivePanel() {
      return this.panels.find((entry) => entry.classList.contains("is-active")) || null;
    }

    getVisibleVideo(panel) {
      if (!panel) return null;
      return Array.from(panel.querySelectorAll(SELECTORS.video)).find((video) => {
        return video.getClientRects().length > 0;
      }) || null;
    }

    playActiveVideos() {
      const panel = this.getActivePanel();
      const video = this.getVisibleVideo(panel);
      if (!video) {
        this.syncProgressBar();
        return;
      }

      video.muted = true;
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch(() => {});
      }
      this.syncProgressBar();
    }

    syncProgressBar() {
      this.stopProgressLoop();

      const panel = this.getActivePanel();
      this.activeProgress = panel?.querySelector(SELECTORS.progress) || null;
      this.activeVideo = this.getVisibleVideo(panel);

      this.updateProgress();

      if (!this.activeVideo || !this.activeProgress) return;
      this.startProgressLoop();
    }

    startProgressLoop() {
      const tick = () => {
        this.updateProgress();
        if (!this.activeVideo || this.activeVideo.paused) {
          this.progressRaf = null;
          return;
        }
        this.progressRaf = requestAnimationFrame(tick);
      };
      this.progressRaf = requestAnimationFrame(tick);
    }

    stopProgressLoop() {
      if (!this.progressRaf) return;
      cancelAnimationFrame(this.progressRaf);
      this.progressRaf = null;
    }

    updateProgress() {
      if (!this.activeProgress) return;

      if (!this.activeVideo || !this.activeVideo.duration) {
        this.activeProgress.style.setProperty("--ctr-science-progress", "0%");
        this.activeProgress.setAttribute("aria-valuenow", "0");
        return;
      }

      const progress = Math.min(100, Math.max(0, (this.activeVideo.currentTime / this.activeVideo.duration) * 100));
      this.activeProgress.style.setProperty("--ctr-science-progress", `${progress}%`);
      this.activeProgress.setAttribute("aria-valuenow", String(Math.round(progress)));
    }

    handleProgressClick(event) {
      event.preventDefault();
      const panel = event.currentTarget.closest(SELECTORS.panel);
      const video = this.getVisibleVideo(panel);
      if (!video || !video.duration) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      video.currentTime = ratio * video.duration;
      this.updateProgress();
    }

    syncDropdown() {
      if (!this.dropdownCurrent) return;
      const activeTab = this.tabs.find((tab) => tab.classList.contains("is-active")) || this.tabs[0];
      if (!activeTab) return;
      this.dropdownCurrent.textContent = activeTab.textContent.trim();
    }

    onSectionSelect(event) {
      const detail = event.detail || {};
      if (detail.sectionId !== this.sectionId) return;
      this.closeNav();
    }

    onBlockSelect(event) {
      const detail = event.detail || {};
      if (detail.sectionId !== this.sectionId || !detail.blockId) return;

      const panel = this.section.querySelector(`[data-block-id="${detail.blockId}"]`);
      if (panel?.dataset.tabPanel) {
        this.setActiveTab(panel.dataset.tabPanel);
      } else {
        this.setActiveTab(detail.blockId);
      }

      this.closeNav();
    }

    onResize() {
      if (window.matchMedia("(min-width: 990px)").matches) {
        this.closeNav();
      }
      this.pauseSectionVideos();
      this.playActiveVideos();
    }

    destroy() {
      this.stopProgressLoop();
      this.pauseSectionVideos();

      this.tabs.forEach((tab) => {
        tab.removeEventListener("click", this.handleTabClick);
        tab.removeEventListener("keydown", this.handleTabKeydown);
      });

      this.videos.forEach((video) => {
        video.removeEventListener("play", this.handleVideoPlay);
        video.removeEventListener("pause", this.handleVideoPause);
      });

      this.progressBars.forEach((progress) => {
        progress.removeEventListener("click", this.handleProgressClick);
      });

      if (this.dropdown) {
        this.dropdown.removeEventListener("click", this.handleDropdownClick);
        document.removeEventListener("click", this.handleDocumentClick);
        document.removeEventListener("keydown", this.handleDocumentKeydown);
      }

      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("shopify:section:select", this.onSectionSelect);
      document.removeEventListener("shopify:section:deselect", this.onSectionSelect);
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
    }
  }

  const init = (root) => {
    const section = root?.querySelector?.(SELECTORS.root) || root;
    if (!section || !section.matches(SELECTORS.root)) return;
    if (instances.has(section)) return;
    instances.set(section, new CtrScience(section));
  };

  const unload = (root) => {
    const section = root?.querySelector?.(SELECTORS.root) || root;
    if (!section || !section.matches(SELECTORS.root)) return;
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  };

  const initAll = () => {
    document.querySelectorAll(SELECTORS.root).forEach((section) => init(section));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => unload(event.target));
  document.addEventListener("shopify:section:reorder", initAll);
})();
